/**
 * **打击音效（合成，不用素材）。**
 *
 * 评审要的那一记 "IMPACT" 里有一半在声音上（`docs/dying-impact.md` §2.2）：
 * 一记**低频下潜**（60 → 28Hz）+ 一层**噪声 crack**（那一下"啪"的触感）。
 *
 * ## 为什么是合成的
 *
 * 项目里只有 BGM 的素材（52MB，`src/audio/assets/zenith`），一个音效素材都没有。
 * 而这两样恰好都是**合成器最擅长**的东西：一条正弦扫频 + 一段带包络的噪声。
 * 用 Tone 现搭既不用挑素材、不用管授权，也不用把音量/时长写死在文件里 ——
 * 参数全在这个文件顶上。
 *
 * ⚠️ 这个模块只在**浏览器**里有意义：`tone` 一 import 就会去摸 AudioContext。
 *    所以它由 `main.ts` 在真正要出声的那条路上创建（和 `bgm` 的 port 一样），
 *    单测和调试页都碰不到它。
 */

import { Filter, Gain, Noise, Oscillator, getContext } from 'tone';

/** 一记打击的声音参数 */
export interface ImpactSfxStyle {
  /** 下潜的起点 / 终点（Hz） */
  sweepFrom: number;
  sweepTo: number;
  /** 下潜多久（秒） */
  sweepSeconds: number;
  /** 下潜的峰值音量（0..1，再乘设置里的音效音量） */
  sweepGain: number;
  /** 起始那一记"啪"的时长（秒） */
  crackSeconds: number;
  crackGain: number;
  /** 噪声的低通截止（Hz）。太高会变成"呲"，太低就听不见了 */
  crackTone: number;
}

export const DEFAULT_IMPACT_SFX: ImpactSfxStyle = {
  sweepFrom: 62,
  sweepTo: 27,
  sweepSeconds: 0.5,
  sweepGain: 0.55,
  crackSeconds: 0.09,
  crackGain: 0.34,
  crackTone: 1800,
};

export interface ImpactSfx {
  /** 放一记。可以在没有音频上下文时安全调用（那就什么都不发生） */
  impact(): void;
  /** 0–1。设置页那一路接过来 */
  setVolume(value: number): void;
  dispose(): void;
}

export function createImpactSfx(style: Partial<ImpactSfxStyle> = {}): ImpactSfx {
  const s: ImpactSfxStyle = { ...DEFAULT_IMPACT_SFX, ...style };
  let volume = 1;
  let disposed = false;
  /** 懒建：第一次真放的时候才碰 Tone（免得首屏就摸 AudioContext） */
  let nodes: {
    sweep: Oscillator;
    crack: Noise;
    crackFilter: Filter;
    sweepGain: Gain;
    crackGain: Gain;
  } | null = null;

  const build = (): typeof nodes => {
    if (nodes !== null) return nodes;
    try {
      const sweepGain = new Gain(0).toDestination();
      const crackGain = new Gain(0).toDestination();
      // 噪声那一路挂一个低通，把"嘶"削成"啪"
      const crackFilter = new Filter(s.crackTone, 'lowpass').connect(crackGain);
      nodes = {
        sweep: new Oscillator({ frequency: s.sweepFrom, type: 'sine' }).connect(sweepGain),
        crack: new Noise('brown').connect(crackFilter),
        crackFilter,
        sweepGain,
        crackGain,
      };
    } catch {
      // 没有可用的音频环境（被策略拦、上下文没起来）：当没这回事
      nodes = null;
    }
    return nodes;
  };

  return {
    impact() {
      if (disposed) return;
      const built = build();
      if (built === null) return;
      const now = getContext().now();
      const level = Math.min(Math.max(volume, 0), 1);
      try {
        // ① 低频下潜：60 → 28Hz，指数滑下去（线性听起来像"稳住了"）
        const osc = built.sweep;
        osc.frequency.cancelScheduledValues(now);
        osc.frequency.setValueAtTime(s.sweepFrom, now);
        osc.frequency.exponentialRampToValueAtTime(s.sweepTo, now + s.sweepSeconds);
        const g = built.sweepGain.gain;
        g.cancelScheduledValues(now);
        g.setValueAtTime(0, now);
        g.linearRampToValueAtTime(s.sweepGain * level, now + 0.012);
        g.exponentialRampToValueAtTime(0.0001, now + s.sweepSeconds);
        // ② 噪声 crack：一记短促的"啪"
        const cg = built.crackGain.gain;
        cg.cancelScheduledValues(now);
        cg.setValueAtTime(s.crackGain * level, now);
        cg.exponentialRampToValueAtTime(0.0001, now + s.crackSeconds);
      } catch {
        // 参数排期失败（上下文被挂起之类）：这一记不响，不算错
      }
    },
    setVolume(value) {
      volume = Number.isFinite(value) ? Math.min(Math.max(value, 0), 1) : 0;
    },
    dispose() {
      disposed = true;
      if (nodes === null) return;
      for (const node of [
        nodes.sweep,
        nodes.crack,
        nodes.crackFilter,
        nodes.sweepGain,
        nodes.crackGain,
      ]) {
        try {
          node.dispose();
        } catch {
          // 已经销毁过了
        }
      }
      nodes = null;
    },
  };
}
